// ──────────────────────────────────────────────────────────────────────
// Batch 5 D4-UI-1 — Supervisor Tree visual.
// Renders the workspace's supervisor/report relationships as a forest
// (tree with multiple roots). Owner/Admin can add a new supervisor link
// or remove an existing one. Each row shows the supervisor + report
// with friendly names. Tree is derived from the flat assignment list
// returned by GET /api/supervisor-tree.
//
// Distinct from the legacy OrgChartCard (which uses workspace_members.
// manager_id — single chain). Supervisor Tree allows MANY supervisors
// per report and transitive visibility for EOD + tasks.
// ──────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Network, Plus, Trash2, ChevronRight, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { StellarSelect } from './StellarFormControls';
import { supervisorTreeAPI, workspaceAPI } from '../lib/api';
import { toast } from 'sonner';

export function SupervisorTreeCard({ workspaceId, viewerRole }) {
  const isEditor = ['super_admin', 'owner', 'admin'].includes(viewerRole);
  const [members, setMembers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newSup, setNewSup] = useState('');
  const [newRep, setNewRep] = useState('');
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [m, a] = await Promise.all([
        workspaceAPI.listMembersLite(workspaceId),
        supervisorTreeAPI.list(workspaceId),
      ]);
      setMembers(m.data || []);
      setAssignments(a.data?.assignments || []);
    } catch (err) {
      console.error('Supervisor tree load', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const memberById = useMemo(() => {
    const map = {};
    members.forEach(m => { map[m.id] = m; });
    return map;
  }, [members]);

  // D57 BUG 4 — Rebuild the forest deterministically:
  //   roots = Owner + true orphans (members with NO supervisor in the
  //           assignments table)
  //   each subordinate appears ONCE under its "primary" supervisor (first
  //           assignment by created_at/id ascending — stable)
  //   secondary supervisors render as small chips on the canonical row
  //
  // This eliminates the previous duplicate Admin roots (where Stellar LTD
  // and Dhrumil both appeared as top-level with Srushti nested under each).
  const tree = useMemo(() => {
    const supsByReport = {};  // report_user_id → [assignment, ...] (in order)
    const childrenBySup = {}; // supervisor_user_id → [assignment]  (only primary)
    const reportsSet = new Set();
    // Order assignments deterministically by created_at then id so the
    // "primary supervisor" pick is stable across renders.
    const ordered = [...assignments].sort((a, b) => {
      const aa = a.created_at || a.id || '';
      const bb = b.created_at || b.id || '';
      return aa < bb ? -1 : aa > bb ? 1 : 0;
    });
    ordered.forEach(a => {
      if (!supsByReport[a.report_user_id]) supsByReport[a.report_user_id] = [];
      supsByReport[a.report_user_id].push(a);
      reportsSet.add(a.report_user_id);
    });
    // Only the FIRST assignment per report becomes the canonical edge.
    Object.values(supsByReport).forEach(arr => {
      const primary = arr[0];
      if (!childrenBySup[primary.supervisor_user_id]) childrenBySup[primary.supervisor_user_id] = [];
      childrenBySup[primary.supervisor_user_id].push(primary);
    });
    // Owner = workspace_members.role === 'owner' (if present in `members`).
    const ownerMember = members.find(m => m.role === 'owner');
    // Roots: include Owner first, then any member who is a supervisor for at
    // least one report AND is NOT themselves a report. Also include true
    // orphans (members with no role in either column) under Owner if owner exists.
    const supervisorIds = new Set(Object.keys(childrenBySup));
    const allRootCandidates = new Set();
    if (ownerMember) allRootCandidates.add(ownerMember.id);
    supervisorIds.forEach(id => { if (!reportsSet.has(id)) allRootCandidates.add(id); });
    const rootIds = Array.from(allRootCandidates);
    // For each report, also record secondary supervisors (everyone except primary).
    const secondaryByReport = {};
    Object.entries(supsByReport).forEach(([rep, arr]) => {
      if (arr.length > 1) secondaryByReport[rep] = arr.slice(1);
    });
    return { childrenBySup, rootIds, secondaryByReport, supsByReport };
  }, [assignments, members]);

  const handleAdd = async () => {
    if (!newSup || !newRep) { toast.error('Choose both a supervisor and a report.'); return; }
    if (newSup === newRep) { toast.error('Cannot supervise yourself.'); return; }
    setSaving(true);
    try {
      await supervisorTreeAPI.create(workspaceId, newSup, newRep);
      toast.success('Supervisor assignment added');
      setShowAdd(false); setNewSup(''); setNewRep('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add');
    } finally { setSaving(false); }
  };

  const handleRemove = async (assignmentId) => {
    setRemovingId(assignmentId);
    try {
      await supervisorTreeAPI.remove(workspaceId, assignmentId);
      toast.success('Assignment removed');
      setAssignments(prev => prev.filter(a => a.id !== assignmentId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove');
    } finally { setRemovingId(null); }
  };

  const renderNode = (userId, depth = 0, seen = new Set()) => {
    if (seen.has(userId)) return null;
    const newSeen = new Set(seen); newSeen.add(userId);
    const m = memberById[userId];
    const label = m?.name || m?.email || userId.slice(0, 8);
    const children = tree.childrenBySup[userId] || [];
    return (
      <div key={userId} style={{ marginLeft: depth * 18 }} className="my-1">
        <div className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-foreground/[0.03]">
          {depth > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground/40 shrink-0" />}
          <div className="h-6 w-6 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
            <span className="text-[10px] font-semibold text-gold">{(label || '?')[0]?.toUpperCase()}</span>
          </div>
          <Link to={`/people/${userId}`} className="text-[13px] font-medium text-foreground/90 hover:text-gold" data-testid={`sup-tree-name-${userId}`}>{label}</Link>
          {children.length > 0 && <span className="text-[10px] text-muted-foreground/40">· {children.length} report{children.length > 1 ? 's' : ''}</span>}
        </div>
        {children.map(c => (
          <div key={c.id} className="relative">
            <div style={{ marginLeft: (depth + 1) * 18 }} className="flex items-center gap-2 py-1 group">
              {renderNodeInline(c, depth + 1, newSeen)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderNodeInline = (assignment, depth, seen) => {
    if (seen.has(assignment.report_user_id)) {
      return <span className="text-[11px] text-amber-400">(cycle: {assignment.report_user_id.slice(0, 8)})</span>;
    }
    const m = memberById[assignment.report_user_id];
    const label = m?.name || m?.email || assignment.report_user_id.slice(0, 8);
    const grandchildren = tree.childrenBySup[assignment.report_user_id] || [];
    // D57 BUG 4 — secondary supervisor chips. If this report ALSO reports
    // to other supervisors (beyond the primary one we rendered them under),
    // surface them as small chips so the relationship isn't lost.
    const secondaries = tree.secondaryByReport[assignment.report_user_id] || [];
    return (
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <ChevronRight className="h-3 w-3 text-muted-foreground/40 shrink-0" />
          <div className="h-5 w-5 rounded-full bg-teal-500/10 flex items-center justify-center shrink-0">
            <span className="text-[9px] font-semibold text-teal-400">{(label || '?')[0]?.toUpperCase()}</span>
          </div>
          <Link to={`/people/${assignment.report_user_id}`} className="text-[12px] text-foreground/80 hover:text-gold" data-testid={`sup-tree-name-${assignment.report_user_id}`}>{label}</Link>
          {grandchildren.length > 0 && <span className="text-[10px] text-muted-foreground/40">· {grandchildren.length} sub-report{grandchildren.length > 1 ? 's' : ''}</span>}
          {secondaries.map(s => {
            const sm = memberById[s.supervisor_user_id];
            const slabel = sm?.name || sm?.email || s.supervisor_user_id.slice(0, 8);
            return (
              <span
                key={s.id}
                className="text-[9px] px-1.5 py-0.5 rounded bg-foreground/[0.04] text-muted-foreground/60 border border-border/20"
                title={`Also reports to ${slabel}`}
                data-testid={`sup-tree-secondary-${s.id}`}
              >
                +{slabel}
              </span>
            );
          })}
          {isEditor && (
            <button
              onClick={() => handleRemove(assignment.id)}
              disabled={removingId === assignment.id}
              className="opacity-0 group-hover:opacity-100 ml-auto p-1 rounded hover:bg-red-500/10 transition-opacity"
              title="Remove this assignment"
              data-testid={`sup-tree-remove-${assignment.id}`}
            >
              {removingId === assignment.id
                ? <Loader2 className="h-3 w-3 animate-spin text-muted-foreground/40" />
                : <Trash2 className="h-3 w-3 text-red-400/70" />}
            </button>
          )}
        </div>
        {grandchildren.map(gc => (
          <div key={gc.id} style={{ marginLeft: 18 }} className="group">
            {renderNodeInline(gc, depth + 1, new Set([...seen, assignment.report_user_id]))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className="bg-card/60 backdrop-blur" data-testid="supervisor-tree-card">
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Network className="h-4 w-4 text-gold" /> Supervisor Tree
          <span className="text-[11px] font-normal text-muted-foreground/50 ml-1">— EOD visibility + multi-supervisor relationships</span>
        </CardTitle>
        {isEditor && (
          <Button
            size="sm"
            onClick={() => setShowAdd(true)}
            className="h-8 text-[12px] bg-gold/10 hover:bg-gold/20 text-gold border-0"
            data-testid="supervisor-tree-add"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Supervisor
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-6 text-[12px] text-muted-foreground/50"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Loading…</div>
        ) : assignments.length === 0 ? (
          <div className="text-center py-6 text-[12px] text-muted-foreground/40" data-testid="supervisor-tree-empty">
            No supervisor assignments yet. {isEditor && 'Click "Add Supervisor" to create the first link.'}
          </div>
        ) : (
          <div className="space-y-0.5">
            {tree.rootIds.map(rootId => renderNode(rootId, 0))}
          </div>
        )}
      </CardContent>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-card border-border/40 max-w-md" data-testid="supervisor-tree-add-dialog">
          <DialogHeader>
            <DialogTitle>Add Supervisor Assignment</DialogTitle>
            <DialogDescription className="text-[12px]">
              The supervisor will be able to see the report's EOD and tasks (transitive — supervisors of supervisors inherit visibility).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-[11px] text-muted-foreground/60 mb-1 block">Supervisor</label>
              <StellarSelect
                value={newSup}
                onValueChange={setNewSup}
                options={members.map(m => ({ value: m.id, label: m.name || m.email }))}
                placeholder="Choose supervisor…"
                testId="sup-tree-sup-select"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground/60 mb-1 block">Report (the person being supervised)</label>
              <StellarSelect
                value={newRep}
                onValueChange={setNewRep}
                options={members.filter(m => m.id !== newSup).map(m => ({ value: m.id, label: m.name || m.email }))}
                placeholder="Choose report…"
                testId="sup-tree-rep-select"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button size="sm" disabled={saving} onClick={handleAdd} className="bg-gold/15 text-gold border-0 hover:bg-gold/25" data-testid="supervisor-tree-add-confirm">
              {saving ? 'Adding…' : 'Add Assignment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default SupervisorTreeCard;
